import { createContext, useContext, ReactNode, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { queryClient } from "@/lib/queryClient";

const DEFAULT_ROLE_LABELS: Record<string, string> = {
  user: "User",
  supervisor: "Supervisor",
  manager: "Manager",
  admin: "Admin",
  owner: "Owner",
};

export function getDefaultRoleLabel(roleKey: string): string {
  if (!roleKey) return "";
  const key = roleKey.toLowerCase();
  return DEFAULT_ROLE_LABELS[key] || roleKey.charAt(0).toUpperCase() + roleKey.slice(1);
}

type RoleLabelsContextType = {
  roleLabels: Record<string, string>;
  getRoleLabel: (roleKey: string) => string;
  isLoading: boolean;
};

const RoleLabelsContext = createContext<RoleLabelsContextType | null>(null);

type LabelCache = {
  companyId: number | null;
  labels: Record<string, string>;
};

export function RoleLabelsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const companyId = user?.companyId ?? null;
  const [labelCache, setLabelCache] = useState<LabelCache>({ companyId: null, labels: {} });

  const { data: customLabels, isLoading } = useQuery<Record<string, string>>({
    queryKey: ["/api/company/role-labels", companyId],
    retry: false,
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });

  useEffect(() => {
    if (customLabels && companyId) {
      setLabelCache({ companyId, labels: customLabels });
    }
  }, [customLabels, companyId]);

  useEffect(() => {
    if (labelCache.companyId !== null && labelCache.companyId !== companyId) {
      queryClient.removeQueries({
        queryKey: ["/api/company/role-labels", labelCache.companyId],
      });
      setLabelCache({ companyId, labels: {} });
    }
  }, [companyId, labelCache.companyId]);

  const effectiveLabels = companyId && labelCache.companyId === companyId ? labelCache.labels : {};

  const getRoleLabel = (roleKey: string): string => {
    if (!roleKey) return "";
    const key = roleKey.toLowerCase();
    return (
      effectiveLabels[key] ||
      DEFAULT_ROLE_LABELS[key] ||
      roleKey.charAt(0).toUpperCase() + roleKey.slice(1)
    );
  };

  const roleLabels = {
    ...DEFAULT_ROLE_LABELS,
    ...effectiveLabels,
  };

  return (
    <RoleLabelsContext.Provider value={{ roleLabels, getRoleLabel, isLoading }}>
      {children}
    </RoleLabelsContext.Provider>
  );
}

export function useRoleLabels() {
  const context = useContext(RoleLabelsContext);
  if (!context) {
    return {
      roleLabels: DEFAULT_ROLE_LABELS,
      getRoleLabel: (roleKey: string) => DEFAULT_ROLE_LABELS[roleKey] || roleKey,
      isLoading: false,
    };
  }
  return context;
}

export { DEFAULT_ROLE_LABELS };
